// src/components/JobSearchBulkActions.tsx

import React, { useState, useEffect } from 'react';
import { useDarkMode } from '../context/DarkModeContext';
import { JobApplicationEnhanced, ApplicationStatus } from '../types/jobSearch';
import { statusConfig } from '../utils/jobApplicationUtils';
import {
    IoCheckboxOutline,
    IoCheckbox,
    IoArchiveOutline,
    IoAlbumsOutline,
    IoArrowForwardOutline
} from 'react-icons/io5';

interface BulkActionsProps {
    selectedApplications: JobApplicationEnhanced[];
    onSelectAll: () => void;
    onDeselectAll: () => void;
    onArchiveSelected: () => void;
    onUpdateStatus: (status: ApplicationStatus) => void;
    onAddTags: (tags: string[]) => void;
    onRemoveTags: (tags: string[]) => void;
    availableTags: string[];
}

const JobSearchBulkActions: React.FC<BulkActionsProps> = ({
    selectedApplications,
    onSelectAll,
    onDeselectAll,
    onArchiveSelected,
    onUpdateStatus,
    onAddTags,
    onRemoveTags,
    availableTags
}) => {
    const { darkMode } = useDarkMode();
    const [showTagMenu, setShowTagMenu] = useState(false);
    const [showStatusMenu, setShowStatusMenu] = useState(false);
    const [newTag, setNewTag] = useState('');
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        setIsVisible(selectedApplications.length > 0);
    }, [selectedApplications.length]);

    if (!isVisible) return null;

    return (
        <div className={`fixed bottom-0 left-0 right-0 ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg p-4`}>
            <div className="max-w-7xl mx-auto flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    <button
                        onClick={selectedApplications.length > 0 ? onDeselectAll : onSelectAll}
                        className="flex items-center space-x-2"
                    >
                        {selectedApplications.length > 0 ? (
                            <IoCheckbox size={20} className="text-blue-500" />
                        ) : (
                            <IoCheckboxOutline size={20} />
                        )}
                        <span>{selectedApplications.length} selected</span>
                    </button>
                </div>

                <div className="flex items-center space-x-4">
                    {/* Status Update */}
                    <div className="relative">
                        <button
                            onClick={() => setShowStatusMenu(!showStatusMenu)}
                            className="flex items-center space-x-2 px-3 py-2 rounded hover:bg-gray-100"
                        >
                            <IoArrowForwardOutline size={20} />
                            <span>Update Status</span>
                        </button>

                        {showStatusMenu && (
                            <div className={`absolute bottom-full mb-2 right-0 ${darkMode ? 'bg-gray-700' : 'bg-white'
                                } shadow-lg rounded-lg p-2 min-w-[200px]`}>
                                {Object.entries(statusConfig).map(([status, config]) => (
                                    <button
                                        key={status}
                                        onClick={() => {
                                            onUpdateStatus(status as ApplicationStatus);
                                            setShowStatusMenu(false);
                                        }}
                                        className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded flex items-center"
                                    >
                                        <span className="mr-2">{config.icon}</span>
                                        {config.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Tag Management */}
                    <div className="relative">
                        <button
                            onClick={() => setShowTagMenu(!showTagMenu)}
                            className="flex items-center space-x-2 px-3 py-2 rounded hover:bg-gray-100"
                        >
                            <IoAlbumsOutline size={20} />
                            <span>Manage Tags</span>
                        </button>

                        {showTagMenu && (
                            <div className={`absolute bottom-full mb-2 right-0 ${darkMode ? 'bg-gray-700' : 'bg-white'
                                } shadow-lg rounded-lg p-2 min-w-[200px]`}>
                                <div className="mb-2">
                                    <input
                                        type="text"
                                        value={newTag}
                                        onChange={(e) => setNewTag(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter' && newTag.trim()) {
                                                onAddTags([newTag.trim()]);
                                                setNewTag('');
                                            }
                                        }}
                                        placeholder="Add new tag..."
                                        className={`w-full p-2 rounded ${darkMode ? 'bg-gray-600' : 'bg-gray-100'}`}
                                    />
                                </div>
                                <div className="max-h-48 overflow-y-auto">
                                    {availableTags.map(tag => (
                                        <button
                                            key={tag}
                                            onClick={() => onAddTags([tag])}
                                            className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded flex items-center justify-between"
                                        >
                                            <span>{tag}</span>
                                            <span className="text-xs text-gray-500">Click to add</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Archive Action */}
                    <button
                        onClick={onArchiveSelected}
                        className="flex items-center space-x-2 px-3 py-2 rounded hover:bg-gray-100"
                    >
                        <IoArchiveOutline size={20} />
                        <span>Archive</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default JobSearchBulkActions;